import React, { useState, useEffect, useRef } from 'react';

const Maintenance = ({ hideOverlay }) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);

  // Mouse tracking for spotlight effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Canvas particle network
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;

    const resize = () => {
      const parent = canvas.parentElement;
      canvas.width = parent ? parent.offsetWidth : window.innerWidth;
      canvas.height = parent ? parent.offsetHeight : window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = [];
    const particleCount = 100;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.6,
        vy: (Math.random() - 0.5) * 0.6,
        radius: Math.random() * 2 + 0.5,
        opacity: Math.random() * 0.6 + 0.2
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(252, 68, 32, ${p.opacity})`;
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[j].x - p.x;
          const dy = particles[j].y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 180) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(252, 68, 32, ${0.12 * (1 - dist / 180)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div style={{
      height: hideOverlay ? '100%' : '100vh',
      width: hideOverlay ? '100%' : '100vw',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'radial-gradient(ellipse at bottom, #0B0F19 0%, #050508 100%)',
      color: '#fff',
      fontFamily: '"Inter", "SF Pro Display", system-ui, -apple-system, sans-serif',
      overflow: 'hidden',
      position: hideOverlay ? 'relative' : 'fixed',
      top: 0,
      left: 0,
      zIndex: hideOverlay ? 1 : 9999
    }}>
      {/* Canvas Particle Network */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1
        }}
      />

      {/* Mouse-following spotlight */}
      <div style={{
        position: 'absolute',
        left: mousePos.x - 400,
        top: mousePos.y - 400,
        width: 800,
        height: 800,
        background: 'radial-gradient(circle, rgba(252, 68, 32, 0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
        transition: 'all 0.5s ease-out',
        zIndex: 2,
        filter: 'blur(80px)'
      }} />

      {/* Animated gradient orbs */}
      <div style={{
        position: 'absolute',
        width: 800,
        height: 800,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(252, 68, 32, 0.15), transparent 70%)',
        top: '-15%',
        left: '-15%',
        animation: 'orbFloat1 25s ease-in-out infinite',
        zIndex: 0
      }} />
      <div style={{
        position: 'absolute',
        width: 700,
        height: 700,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(252, 68, 32, 0.1), transparent 70%)',
        bottom: '-20%',
        right: '-15%',
        animation: 'orbFloat2 30s ease-in-out infinite',
        zIndex: 0
      }} />
      <div style={{
        position: 'absolute',
        width: 600,
        height: 600,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 123, 84, 0.08), transparent 70%)',
        top: '50%',
        left: '50%',
        animation: 'orbFloat3 22s ease-in-out infinite',
        zIndex: 0
      }} />

      {/* Scanline overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 4px)',
        pointerEvents: 'none',
        zIndex: 3
      }} />

      {/* Main Content */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 32,
        animation: 'fadeInUp 1.2s ease-out'
      }}>
        {/* Animated rings with icon */}
        <div style={{
          position: 'relative',
          width: 180,
          height: 180,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{
              position: 'absolute',
              width: 180 - i * 45,
              height: 180 - i * 45,
              border: `1px solid ${i === 0 ? 'rgba(252, 68, 32, 0.25)' : i === 1 ? 'rgba(255, 123, 84, 0.25)' : 'rgba(255, 255, 255, 0.1)'}`,
              borderRadius: '50%',
              animation: `rotate${i % 2 === 0 ? 'CW' : 'CCW'} ${12 + i * 6}s linear infinite`,
              borderTop: `2px solid ${i === 0 ? '#FC4420' : i === 1 ? '#ff7b54' : '#ffffff'}`,
              borderRight: '1px solid transparent'
            }} />
          ))}

          {/* Center icon */}
          <div style={{
            width: 70,
            height: 70,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(252, 68, 32, 0.15), rgba(255, 123, 84, 0.15))',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            animation: 'float 5s ease-in-out infinite'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="url(#iconGrad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <defs>
                <linearGradient id="iconGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FC4420" />
                  <stop offset="100%" stopColor="#ff7b54" />
                </linearGradient>
              </defs>
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: 'clamp(36px, 7vw, 64px)',
          fontWeight: 800,
          letterSpacing: '-0.03em',
          margin: 0,
          background: 'linear-gradient(135deg, #fff 0%, #FC4420 50%, #ff7b54 100%)',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          animation: 'gradientShift 5s ease infinite',
          textAlign: 'center'
        }}>
          Under Maintenance
        </h1>

        {/* Subtitle */}
        <p style={{
          fontSize: 17,
          color: 'rgba(255, 255, 255, 0.45)',
          lineHeight: 1.7,
          maxWidth: 420,
          textAlign: 'center',
          margin: 0,
          fontWeight: 400
        }}>
          We're upgrading our systems to serve you better.
          Please check back shortly.
        </p>

        {/* Status indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 24px',
          background: 'rgba(252, 68, 32, 0.08)',
          border: '1px solid rgba(252, 68, 32, 0.2)',
          borderRadius: 50,
          backdropFilter: 'blur(10px)',
          marginTop: 8
        }}>
          <div style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#FC4420',
            boxShadow: '0 0 10px #FC4420, 0 0 20px rgba(252, 68, 32, 0.5)',
            animation: 'blink 2s ease-in-out infinite'
          }} />
          <span style={{
            fontSize: 12,
            letterSpacing: 3,
            color: '#FC4420',
            fontWeight: 600,
            textTransform: 'uppercase'
          }}>
            System Upgrade in Progress
          </span>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        @keyframes orbFloat1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(100px, 80px) scale(1.1); }
          66% { transform: translate(-60px, 100px) scale(0.9); }
        }

        @keyframes orbFloat2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-100px, -80px) scale(1.2); }
        }

        @keyframes orbFloat3 {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-40%, -60%) scale(1.1); }
        }

        @keyframes rotateCW {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes rotateCCW {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        @keyframes gradientShift {
          0%, 100% { background-position: 0% center; }
          50% { background-position: 100% center; }
        }

        @keyframes blink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      ` }} />
    </div>
  );
};

export default Maintenance;
